import type { SyncStorageService } from '.';
import { injectable } from 'inversify';

/**
 * Web implementation of SyncStorageService that wraps localStorage.
 * Used in browser environments where localStorage is available.
 *
 * Returns a Proxy so that bracket notation access (e.g., storage['key'] = value)
 * is forwarded to localStorage. This is required because @axa-fr/oidc-client
 * uses bracket notation instead of getItem/setItem methods.
 */
@injectable()
export class WebSyncStorageService implements SyncStorageService {
    constructor() {
        // Return a Proxy that intercepts property access and delegates to localStorage
        // This allows both method calls (getItem/setItem) and bracket notation (storage['key'])
        return new Proxy(this, {
            get(target, prop: string) {
                // Handle methods and properties defined on the class
                if (prop in target) {
                    return target[prop as keyof typeof target];
                }
                // For any other property, delegate to localStorage
                return localStorage.getItem(prop);
            },
            set(_target, prop: string, value: string) {
                localStorage.setItem(prop, value);
                return true;
            },
            deleteProperty(_target, prop: string) {
                localStorage.removeItem(prop);
                return true;
            },
        });
    }

    getItem(key: string): string | null {
        return localStorage.getItem(key);
    }

    setItem(key: string, value: string): void {
        localStorage.setItem(key, value);
    }

    removeItem(key: string): void {
        localStorage.removeItem(key);
    }

    clear(): void {
        localStorage.clear();
    }

    get length(): number {
        return localStorage.length;
    }

    key(index: number): string | null {
        return localStorage.key(index);
    }
}
